import React, { useContext, useMemo, useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router";
import { Link, useSearchParams } from "react-router-dom";
import { AuthContext, IntlContext, FlashContext } from "context";
import { Form } from "react-final-form";
import Button from "ui/Button";
import arrayMutators from "final-form-arrays";
import OurServicesResumeInformation from "./Informations";
import OurServicesResumeInvoice from "./Invoice";
import { Textarea } from "components/Form/Field";
import { BookingContext } from "../Context";
import dataFetcher from "hooks/bookService";
import dataFetcherBooking from "hooks/booking";
import { fetchProfileCompanyLimit } from "hooks/profile";
import formatErrorResponse from "helpers/form/formatErrorResponse";
import { getErrorResponseData, getErrorStatus, hasKey, isRecord } from "helpers/typing";
import AdditionalFields from "./Fields";
import { Role } from "types/user";
import roundNumber from "helpers/roundNumber";
import CalculOpperator from "helpers/CalculOpperator";
import { activateAici, activateCesuPlus } from "hooks/cesu";
import Footer from "components/Layout/Main/Footer";
import { useService } from "hooks/service";
import { buildRoute, RESERVATION_STEPS, ROUTES } from "routes";

const OurServicesResume: React.FC = () => {
    const intl = useContext(IntlContext);
    const { account, currentRole, selectRole } = useContext(AuthContext);
    const { id } = useParams();
    const [searchParams] = useSearchParams();
    const { getBooking, getDataAccount, setBooking, getService, setService } = useContext(BookingContext);
    const navigate = useNavigate();
    
    const currentBookingId = localStorage.getItem("current_booking_id") ?? undefined;
    const booking = useQuery({
        queryKey: ["booking", currentBookingId],
        queryFn: () => dataFetcherBooking.one(currentBookingId as string),
        enabled: !!currentBookingId,
    });
    const { data: serviceDataFetched } = useService(id);

    useEffect(() => {
        if(booking.isError) {
            localStorage.removeItem('current_booking_id');
            navigate(id ? buildRoute.provider(String(id)) : ROUTES.HOME);
        } else {
            setBooking(booking.data);
        }

    }, [booking, id, navigate, setBooking]);

    useEffect(() => {
        if (!getService() && serviceDataFetched) {
            setService(serviceDataFetched);
        }

    }, [getService, serviceDataFetched, setService]);

    const bookingData = useMemo(() => {
        return getBooking();
    }, [getBooking]);
    const serviceData = useMemo(() => {
        return getService();
    }, [getService]);
    const dataCompanyLimitUse = useQuery({
        queryKey: ["profile-company-limit"],
        queryFn: fetchProfileCompanyLimit,
    });
    const [notFoundError, setNotFoundError] = useState<boolean>(false);
    const flashMessage = useContext(FlashContext);
    const [isBookLoading, setIsBookLoading] = useState<boolean>(false);

    const initialValues = useMemo(() => {
        let data = {
            client_first_name: account?.user.first_name,
            gender: account?.user.gender,
            client_last_name: account?.user.last_name,
            client_phone: account?.user.phone,
            client_address: account?.user.address,
            client_house_number: account?.user.house_number,
            client_street: account?.user.street,
            client_postcode: account?.user.postcode,
            client_city: account?.user.city,
            client_coordinates_lat: account?.user.coordinates.lat,
            client_coordinates_lon: account?.user.coordinates.lon,
            same_address_invoice: ["1"],
            booking_id: bookingData?.id,
            provider_id: bookingData?.provider_id,
            provider_cesu: serviceData?.provider.num_cesu,
            cesu_plus_status: false,
            attestation_generated: false,
            aici_status: false
        };

        if (bookingData && dataCompanyLimitUse.data) {
            const employee_contributions_ef = parseFloat(bookingData.employee_contributions_ef ?? 0);
            const employer_contributions = parseFloat(bookingData.employer_contributions ?? 0);
            const opperator = new CalculOpperator(bookingData);
            const totalPrice = parseFloat(bookingData.price) * bookingData.nb_hours;
            let [managementFeesValue, totalAmount] = opperator.computeTTC(totalPrice);
            let supportedCompany = 0;
            let supportedCompanyPercent = 0;
            let companyLimitUse = Number(dataCompanyLimitUse.data.companyLimitUse);
            let companyLimit = Number(dataCompanyLimitUse.data.companyLimit);

            if (
                account?.resources?.client &&
                account?.resources?.client[0].company_percentage &&
                companyLimitUse < companyLimit
            ) {
                supportedCompanyPercent = parseFloat(account?.resources?.client[0].company_percentage);
                supportedCompany = totalAmount * (supportedCompanyPercent / 100);

                if (companyLimitUse + supportedCompany >= companyLimit) {
                    supportedCompany -= roundNumber(companyLimitUse + supportedCompany - companyLimit);
                }
            }

            totalAmount = totalAmount - supportedCompany;

            data = Object.assign({}, data, {
                category: bookingData.category,
                rate_hour: totalPrice,
                nb_hours: bookingData.nb_hours,
                management_fees: roundNumber(managementFeesValue),
                supported_company: supportedCompanyPercent,
                initial_total_amount: roundNumber(totalAmount),
                employee_contributions_ef,
                employer_contributions
            });
        }

        return data;
    }, [
        dataCompanyLimitUse.data,
        account?.user.first_name,
        account?.user.gender,
        account?.user.last_name,
        account?.user.phone,
        account?.user.address,
        account?.user.house_number,
        account?.user.street,
        account?.user.postcode,
        account?.user.city,
        account?.user.coordinates.lat,
        account?.user.coordinates.lon,
        account?.resources?.client,
        bookingData,
        serviceData
    ]);

    const backToAvailability = async () => {
        const fromAds = searchParams.get("fromAds");
        if (fromAds) {
            navigate(buildRoute.myAdApplications(fromAds));
        } else {
            navigate(id ? buildRoute.provider(String(id)) : ROUTES.HOME);
        }
    };

    const onSubmit = async (data: Record<string, unknown>) => {
        setNotFoundError(false);

        if (bookingData?.id) {
            setIsBookLoading(true);

            try {
                const email = getDataAccount()?.email;
                const password = getDataAccount()?.password;

                if (email && password) {
                    data = Object.assign({}, data, { email: email, password: password });
                }

                let routes = [];

                if (Boolean(data?.activate_cesu_plus) && data.activate_cesu_plus === '1' && data?.provider_cesu) {
                    if(!data?.cesu_plus_status) {
                        routes.push(await activateCesuPlus(bookingData.id));
                    }

                    if(!data?.aici_status) {
                        routes.push(await activateAici(bookingData.id));
                    }
                }

                Promise.all(routes).then(async () => {
                    let bookingUpdated = await dataFetcher.updateBookSlots(bookingData.id, data);
                    setBooking(bookingUpdated);

                    if (currentRole === Role.PROVIDER) {
                        selectRole(Role.CLIENT);
                    }

                    navigate(buildRoute.providerReservation(String(id), RESERVATION_STEPS.PAYMENT));
                }).catch((err: unknown) => {
                    setIsBookLoading(false);
                    const errData = getErrorResponseData(err);
                    if (
                        errData &&
                        isRecord(errData.data) &&
                        hasKey(errData.data, "data") &&
                        isRecord(errData.data.data) &&
                        hasKey(errData.data.data, "data") &&
                        isRecord(errData.data.data.data)
                    ) {
                        flashMessage?.addError(
                            intl.formatMessage("service.label."+Object.keys(errData.data.data.data)[0]) +
                                " : " +
                                intl.formatMessage("validation.required")
                        );
                    }
                    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
                });
            } catch (err: unknown) {
                setIsBookLoading(false);
                const errData = getErrorResponseData(err);
                if (errData) {
                    if(getErrorStatus(err) === 404) {
                        setNotFoundError(true);
                    } else {
                        let error = "";
            
                        if(
                            isRecord(errData.data) &&
                            hasKey(errData.data, "error") &&
                            isRecord(errData.data.error) &&
                            typeof errData.data.error.description === "string"
                        ) {
                            const errDesc = JSON.parse(errData.data.error.description);
                            error = errDesc.description;
                        } else {
                            error = "";
                        }
                        
                        flashMessage?.addError(intl.formatMessage("our_services.booking.confirm.message.transmitting_cesu.error")
                        + " Raison : \"" + error + "\"");

                        if(error.startsWith("Blocage Cesu+ pour le sala")) {
                            flashMessage?.addError("Impossible d'activer le CESU+ : le salarié n'a pas encore renseigné ses coordonnées bancaires sur son compte CESU.");
                        }

                        if(error.startsWith("codeCommuneNaissance est invalide")) {
                            flashMessage?.addError("Impossible d'activer l'Avance Immédiate de Crédit d'Impôt pour le moment : veuillez activer ce service directement depuis votre compte CESU.");
                        }

                        window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
                    }

                    return formatErrorResponse(errData);
                }
                throw err;
            }
        }
    };

    return (
        <React.Fragment>
            <div className="our_services">
                <div className="block-header">
                    <div className="container">
                        <h1 className="block-header__title">Réservation d'une <span>prestation</span></h1>
                    </div>
                </div>
                <div className="container">
                    <Form
                        onSubmit={onSubmit}
                        initialValues={initialValues}
                        mutators={{ ...arrayMutators }}
                        render={({ handleSubmit, values, form }) => (
                            <form onSubmit={handleSubmit} className="login__form margin--0">
                                <div className="our_services_view_container top-shadow">
                                    <Button className="btn btn--secondary btn--square btn--return" onClick={backToAvailability}>
                                        <span className="icon icon-arrow-left"></span>
                                        {intl.formatMessage("generic.action.back")}
                                    </Button>
                                    <Link
                                        className="btn btn--primary btn--square"
                                        target="_blank"
                                        to={buildRoute.messaging(
                                            String(serviceData?.id ?? ""),
                                            String(account?.resources?.client[0].id ?? ""),
                                        )}
                                        style={{position:"absolute", right:"16px", top:"16px"}}
                                    >
                                        <span className="icon icon-message" style={{marginTop:"-2px", marginRight:"4px"}}></span> Contacter {serviceData?.provider.first_name}
                                    </Link>
                                    <div className="grid--1 grid--medium-1 grid--small-1 grid--has-gutter-3x">
                                        <div className="container_form_intermediate_page">
                                            <div className="container_form_intermediate_page padding--3">
                                                <AdditionalFields />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="grid--12 grid--medium-1 grid--small-1 grid--has-gutter-3x padding-top--3">
                                        <div className="container_form_intermediate_page padding--3 grid__col--6 grid__col--small-12">
                                            <OurServicesResumeInformation />
                                        </div>
                                        <div className="container_form_intermediate_page padding--3 grid__col--6 grid__col--small-12">
                                            <OurServicesResumeInvoice />
                                        </div>
                                    </div>
                                    <div className="grid--1 grid--medium-1 grid--small-1 grid--has-gutter-3x padding-top--3">
                                        <div className="container_form_intermediate_page padding--3">
                                            <Textarea
                                                inputProps={{
                                                    name: "booking_comment",
                                                    required: true,
                                                    className: "field__textarea bg--white"
                                                }}
                                                labelProps={{
                                                    label: intl.formatMessage("our_services.form.label.booking_comment"),
                                                }}
                                            />
                                        </div>
                                    </div>
                                    <div className="text-center margin-top--5">
                                        <>
                                            {(!form.getFieldState("activate_cesu_plus") ||
                                                form.getFieldState("activate_cesu_plus")?.value === "0" ||
                                                values.cesu_plus_status ||
                                                (form.getFieldState("activate_cesu_plus")?.value === "1" &&
                                                    values.attestation_generated)) && (
                                                <Button type="submit" className="btn btn--primary" disabled={isBookLoading}>
                                                    {intl.formatMessage("our_services.form.action.book_confirm")}
                                                    {isBookLoading ? <div className="loader-inner" /> : null}
                                                </Button>
                                            )}

                                            {notFoundError && (
                                                <div className="text-color--danger margin-top--1">
                                                    Réservation introuvable
                                                </div>
                                            )}
                                        </>
                                    </div>
                                </div>
                            </form>
                        )}
                    />
                </div>
            </div>
            <Footer />
        </React.Fragment>
    );
};

export default OurServicesResume;
